import { defineStore } from "pinia"
import { EvercamApi } from "@evercam/shared/api/evercamApi"
import {
  AconexUserProjectsResponsePayload,
  ProcoreUserProjectsResponsePayload,
  ProcoreProjectsAlbumsPayload,
  ProcoreObservationForm,
  Connector,
  ConnectorDetail,
  AutodeskUserHubsResponsePayload,
  AutodeskProjectsResponsePayload,
  AutodeskFolderResponsePayload,
  AnalyticsEvent,
} from "@evercam/shared/types"
import { useNuxtApp } from "#app"
import { AxiosError } from "axios"
import axios from "axios"
import Vue from "vue"

type ConnectorState = {
  connectorPromptDialog: boolean
  selectedConnector: Connector
  isProcoreConnected: boolean
  isAconexConnected: boolean
  isExpiredConnectorToken: boolean
  isAutodeskConnected: boolean
  isVoyageControlConnected: boolean
  isProcoreAlbumFetchFailed: boolean
  aconexConnectorDetails: ConnectorDetail | null
  procoreConnectorDetails: ConnectorDetail | null
  autodeskConnectorDetails: ConnectorDetail | null
  voyageControlConnectorDetails: ConnectorDetail | null
  autodeskViewerConnectorDetails: ConnectorDetail | null
  procoreUserProjects: ProcoreUserProjectsResponsePayload
  procoreProjectAlbums: ProcoreProjectsAlbumsPayload
  procoreObservationTypes: Array<string>
  procoreObservationForm: ProcoreObservationForm
  procoreContributingBehaviors: Array<string>
  procoreContributingConditions: Array<string>
  procoreHazards: Array<string>
  procoreTrades: Array<string>
  procoreSpecificationSections: Array<string>
  procoreLocations: Array<string>
  procoreCustomFieldOptions: Record<string, Array<any>>
  procoreDistributionMembers: Array<any>
  procoreAssignees: Array<any>
  aconexUserProjects: AconexUserProjectsResponsePayload
  autodeskHubs: AutodeskUserHubsResponsePayload
  autodeskProjects: AutodeskProjectsResponsePayload
  autodeskFolders: Array<AutodeskFolderResponsePayload>
  uploadImageData: string
}

export const useConnectorStore = defineStore("connector", {
  state: (): ConnectorState => ({
    connectorPromptDialog: false,
    selectedConnector: null,
    isProcoreConnected: false,
    isAconexConnected: false,
    isAutodeskConnected: false,
    isExpiredConnectorToken: false,
    isVoyageControlConnected: false,
    isProcoreAlbumFetchFailed: false,
    aconexConnectorDetails: null,
    procoreConnectorDetails: null,
    autodeskConnectorDetails: null,
    autodeskViewerConnectorDetails: null,
    voyageControlConnectorDetails: null,
    procoreUserProjects: [],
    procoreProjectAlbums: [],
    procoreObservationTypes: [],
    procoreObservationForm: null,
    procoreContributingBehaviors: [],
    procoreHazards: [],
    procoreContributingConditions: [],
    procoreTrades: [],
    procoreSpecificationSections: [],
    procoreLocations: [],
    procoreCustomFieldOptions: {} as Record<string, Array<any>>,
    procoreDistributionMembers: [],
    procoreAssignees: [],
    aconexUserProjects: [],
    autodeskHubs: [],
    autodeskProjects: [],
    autodeskFolders: [],
    uploadImageData: "",
  }),
  actions: {
    async fetchAutodeskHubs() {
      this.autodeskHubs = []
      if (!this.isAutodeskConnected) {
        return
      }
      try {
        this.autodeskHubs = await EvercamApi.autodesk.getUserHubs()
      } catch (error) {
        this.handleConnectorRequestError(
          error as AxiosError,
          Connector.Autodesk
        )
      }
    },
    async fetchAutodeskProjects(hubId) {
      this.autodeskProjects = []
      if (!this.isAutodeskConnected) {
        return
      }

      this.autodeskProjects = await EvercamApi.autodesk.getProjects(hubId)
    },
    async fetchAutodeskFolders(hubId: string, projectId: string) {
      this.autodeskFolders = []
      if (!this.isAutodeskConnected) {
        return
      }

      this.autodeskFolders = await EvercamApi.autodesk.getFolders(
        hubId,
        projectId
      )
    },

    async fetchAutodekFolder(projectId: string, folderId: string) {
      if (!this.isAutodeskConnected) {
        return
      }

      return await EvercamApi.autodesk.getFolder(projectId, folderId)
    },
    async fetchAutodeskSubfolder(
      item: AutodeskFolderResponsePayload,
      projectId: string,
      getDerivatives: Boolean = false
    ) {
      const children = await EvercamApi.autodesk.getSubFolders(
        projectId,
        item?.value,
        getDerivatives
      )
      if (children?.length) {
        children.forEach(
          (child) =>
            (child.parentName = (item?.parentName ?? "") + item.text + "/")
        )
        item.children = children
      } else {
        item.children = undefined
      }

      return item
    },
    async getUserCompanies() {
      if (!this.isProcoreConnected) {
        this.procoreUserProjects = []

        return
      }
      try {
        const userProcoreCompanies = await EvercamApi.procore.getUserCompanies()
        this.getProcoreUserProjects(userProcoreCompanies)
      } catch (error) {
        this.handleConnectorRequestError(error as AxiosError, Connector.Procore)
      }
    },
    handleConnectorRequestError(error: AxiosError, connectorType: Connector) {
      if (
        error.response?.data?.code === "INVALID_TOKEN" ||
        error.response?.data?.message ===
          `Expired ${connectorType.toLowerCase()} token`
      ) {
        this.openConnectorPromptDialog(connectorType)
        this.isExpiredConnectorToken = true
        this.getConnectors()
      }
    },
    async getProcoreUserProjects(userProcoreCompanies) {
      this.procoreUserProjects = []
      for (const company of userProcoreCompanies) {
        try {
          const projects = await EvercamApi.procore.getUserProjects({
            companyId: company.id,
          })
          this.procoreUserProjects = [...this.procoreUserProjects, ...projects]
        } catch (error) {
          console.error(error)
        }
      }
    },
    async getProcoreProjectAlbums(payload) {
      if (!this.isProcoreConnected) {
        this.procoreProjectAlbums = []

        return
      }
      try {
        this.procoreProjectAlbums = await EvercamApi.procore.getProjectAlbums(
          payload
        )
        this.isProcoreAlbumFetchFailed = false
      } catch (error) {
        this.procoreProjectAlbums = []
        this.isProcoreAlbumFetchFailed = true
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_project_albums_failed"
          ) as string,
        })
      }
    },
    async fetchProcoreObservationForm(payload) {
      if (!this.isProcoreConnected) {
        this.procoreProjectAlbums = []

        return
      }
      try {
        this.procoreObservationForm =
          await EvercamApi.procore.getObservationForm(payload)
      } catch (error) {
        this.procoreObservationForm = null
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_observation_form_failed"
          ) as string,
        })
      }
    },
    async fetchProcoreObservationTypes(payload) {
      if (!this.isProcoreConnected) {
        this.procoreProjectAlbums = []

        return
      }
      try {
        this.procoreObservationTypes =
          await EvercamApi.procore.getObservationTypes(payload)
      } catch (error) {
        this.procoreObservationTypes = null
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_observation_types_failed"
          ) as string,
        })
      }
    },
    fetchProcoreContributingBehaviors(payload) {
      try {
        EvercamApi.procore
          .getContributingBehaviors(payload)
          .then((behaviors) => {
            this.procoreContributingBehaviors = behaviors
          })
      } catch (error) {
        this.procoreContributingBehaviors = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_contributing_behaviors_failed"
          ) as string,
        })
      }
    },
    fetchProcoreHazards(payload) {
      try {
        EvercamApi.procore.getHazards(payload).then((hazards) => {
          this.procoreHazards = hazards
        })
      } catch (error) {
        this.procoreHazards = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_hazards_failed"
          ) as string,
        })
      }
    },
    fetchProcoreContributingConditions(payload) {
      try {
        EvercamApi.procore
          .getContributingConditions(payload)
          .then((conditions) => {
            this.procoreContributingConditions = conditions
          })
      } catch (error) {
        this.procoreContributingConditions = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_contributing_conditions_failed"
          ) as string,
        })
      }
    },
    fetchProcoreTrades(payload) {
      try {
        EvercamApi.procore.getTrades(payload).then((trades) => {
          this.procoreTrades = trades
        })
      } catch (error) {
        this.procoreTrades = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_trades_failed"
          ) as string,
        })
      }
    },
    fetchProcoreSpecificationSections(payload) {
      try {
        EvercamApi.procore
          .getSpecificationSections(payload)
          .then((sections) => {
            this.procoreSpecificationSections = sections
          })
      } catch (error) {
        this.procoreSpecificationSections = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_specification_sections_failed"
          ) as string,
        })
      }
    },
    fetchProcoreLocations(payload) {
      try {
        EvercamApi.procore.getLocations(payload).then((locations) => {
          this.procoreLocations = locations
        })
      } catch (error) {
        this.procoreLocations = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_locations_failed"
          ) as string,
        })
      }
    },
    fetchProcoreCustomFieldOptions(field, payload) {
      try {
        EvercamApi.procore.getCustomFieldOptions(payload).then((options) => {
          Vue.set(this.procoreCustomFieldOptions, field, options)
        })
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_custom_field_options_failed"
          ) as string,
        })
      }
    },
    fetchProcoreDistributionMembers(payload) {
      try {
        EvercamApi.procore.getDistributionMembers(payload).then((members) => {
          this.procoreDistributionMembers = members
        })
      } catch (error) {
        this.procoreDistributionMembers = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_distribution_members_failed"
          ) as string,
        })
      }
    },
    fetchProcoreAssignees(payload) {
      try {
        EvercamApi.procore.getAssignees(payload).then((assignees) => {
          this.procoreAssignees = assignees
        })
      } catch (error) {
        this.procoreAssignees = []
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_fetch_assignees_failed"
          ) as string,
        })
      }
    },
    async getAconexUserProjects() {
      if (!this.isAconexConnected) {
        this.aconexUserProjects = []

        return
      }
      try {
        this.aconexUserProjects = await EvercamApi.aconex.getUserProjects()
      } catch (error) {
        this.handleConnectorRequestError(error as AxiosError, Connector.Aconex)
      }
    },
    async getConnectors() {
      const providers = await EvercamApi.providers.getProviders()
      const aconex = providers.find(
        (item) => item.provider === Connector.Aconex
      )
      const procore = providers.find(
        (item) => item.provider === Connector.Procore
      )
      const autodesk = providers.find(
        (item) => item.provider === Connector.Autodesk
      )
      const voyageControl = providers.find(
        (item) => item.provider === Connector.VoyageControl
      )
      this.isAconexConnected = !!aconex
      this.aconexConnectorDetails = aconex
      this.isProcoreConnected = !!procore
      this.procoreConnectorDetails = procore
      this.isAutodeskConnected = !!autodesk
      this.autodeskConnectorDetails = autodesk
      this.isVoyageControlConnected = !!voyageControl
      this.voyageControlConnectorDetails = voyageControl
      this.getAconexUserProjects()
      this.getUserCompanies()
      this.fetchAutodeskHubs()
    },
    async revokeProcoreConnection() {
      try {
        await EvercamApi.procore.sendRevoke()
        await this.getConnectors()
        useNuxtApp().nuxt2Context.$notifications.success(
          useNuxtApp().vue2App.$i18n.t(
            "content.connectors.procore_remove_success"
          ) as string
        )
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.revoke_procore_failed"
          ) as string,
          error,
        })
      }
    },
    async revokeAconexConnection() {
      try {
        await EvercamApi.aconex.revoke()
        await this.getConnectors()
        useNuxtApp().nuxt2Context.$notifications.success(
          useNuxtApp().vue2App.$i18n.t(
            "content.connectors.aconex_remove_success"
          ) as string
        )
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.aconex_remove_error"
          ) as string,
          error,
        })
      }
    },
    async revokeAutodeskConnection() {
      try {
        await EvercamApi.autodesk.sendRevoke()
        await this.getConnectors()
        useNuxtApp().nuxt2Context.$notifications.success(
          useNuxtApp().vue2App.$i18n.t(
            "content.connectors.autodesk_remove_success"
          ) as string
        )
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.revoke_autodesk_failed"
          ) as string,
          error,
        })
      }
    },
    async revokeVoyageControlConnection() {
      try {
        await EvercamApi.voyageControl.sendRevoke()
        await this.getConnectors()
        useNuxtApp().nuxt2Context.$notifications.success(
          useNuxtApp().vue2App.$i18n.t(
            "content.connectors.voyage_control_remove_success"
          ) as string
        )
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.connectors.revoke_voyage_control_failed"
          ) as string,
          error,
        })
      }
    },
    async setForgeModelId(projectId, item) {
      const nuxt = useNuxtApp()
      try {
        await axios.patch(
          `${nuxt.$config.public.firebaseDbLink}data/projects/forge/${projectId}.json`,
          JSON.stringify({
            urn: item.value,
          })
        )
        nuxt.nuxt2Context.$notifications.success(
          nuxt.vue2App.$i18n.t(
            "content.connectors.autodesk_model_change_success"
          ) as string
        )
      } catch (error) {
        nuxt.nuxt2Context.$notifications.error({
          text: nuxt.vue2App.$i18n.t(
            "content.connectors.autodesk_model_change_error"
          ) as string,
          error,
        })
      }
    },
    handle401Error(error: AxiosError) {
      const url = error.config?.url

      if (url.includes(Connector.Procore)) {
        this.isProcoreConnected = false
      } else if (url.includes(Connector.Aconex)) {
        this.isAconexConnected = false
      } else if (url.includes(Connector.Autodesk)) {
        this.isAutodeskConnected = false
      }
    },
    openConnectorPromptDialog(connector) {
      this.connectorPromptDialog = true
      this.selectedConnector = connector
      useNuxtApp().nuxt2Context.$analytics.saveEvent(
        AnalyticsEvent.SettingsConnectorsToggleConnectorPromptDialog,
        { visible: true, connector: this.selectedConnector }
      )
    },
    closeConnectorPomptDialog() {
      useNuxtApp().nuxt2Context.$analytics.saveEvent(
        AnalyticsEvent.SettingsConnectorsToggleConnectorPromptDialog,
        { visible: false, connector: this.selectedConnector }
      )
      this.connectorPromptDialog = false
      this.selectedConnector = null
    },
  },
  getters: {
    isConnectionError: () => (error: AxiosError) => {
      const url = error.config?.url

      return Object.values(Connector).some((connector) =>
        url.includes(connector)
      )
    },
  },
})
